/**
 * Reconcile a recovery snapshot into the React Query cache.
 *
 * Called on every successful WebSocket (re)connect. The snapshot
 * is the authoritative current state for alerts + reunification at
 * `server_ts`; this function replaces local state with it instead of
 * layering on top. Completed reunifications, ended alerts, released
 * students, and departed guardians that transitioned while the client
 * was offline fall out of the cache as a result.
 *
 * Design:
 *  - `setQueryData` on the keys the reunification handlers invalidate,
 *    writing the snapshot payload directly so a refetch isn't required.
 *  - `invalidateQueries` for downstream keys the snapshot doesn't carry
 *    (`my-students`, notifications) so they refresh against the server.
 *  - Reunification survives an ended alert per ADR-106 §4 — the backend
 *    returns ACTIVE/PAUSED events regardless of parent-alert state, so
 *    nothing extra is needed here.
 *  - Dedupe against replayed WS frames is by identity: the reunification
 *    handlers are pure invalidation, so a replayed frame is idempotent.
 *    The caller guards double-fetch via a per-connect generation.
 */

import type { QueryClient } from '@tanstack/react-query';
import type { HandlerDeps } from './types';
import type {
  SnapshotPayload,
  SnapshotAlertHistory,
  SnapshotRoomStatus,
} from '../../services/snapshot';
import type {
  PaginatedResponse,
  ReunificationEventSummary,
  StudentReunification,
  GuardianSelf,
} from '../../services/reunification';
import type { GuardianStatus } from '../../types/reunification';

function wrapPaginated<T>(results: T[]): PaginatedResponse<T> {
  return { count: results.length, next: null, previous: null, results };
}

function reconcileEvents(
  queryClient: QueryClient,
  event: ReunificationEventSummary | null,
) {
  const active = wrapPaginated(event ? [event] : []);
  // Keep shapes aligned with `useActiveEvents` / `useAllEvents` consumers.
  queryClient.setQueryData(['events', 'active'], active);
  queryClient.setQueryData(['events', 'all'], active);

  if (event) {
    queryClient.setQueryData(['event', event.public_id], event);
  }
  // Completed/absent events: invalidate the per-id detail keys so any
  // open detail view refetches and surfaces the 404/completed state.
  queryClient.invalidateQueries({
    queryKey: ['event'],
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === 'event' &&
        key.length === 2 &&
        key[1] !== event?.public_id
      );
    },
  });
}

function reconcileStudents(
  queryClient: QueryClient,
  event: ReunificationEventSummary | null,
  students: StudentReunification[],
) {
  if (!event) {
    // No active reunification — clear any per-event student caches.
    queryClient.removeQueries({
      queryKey: ['event-students'],
      exact: false,
    });
    // `my-students` is guardian-scoped and independent of a specific event;
    // refetch to pick up any offline status changes.
    queryClient.invalidateQueries({ queryKey: ['my-students'] });
    return;
  }

  queryClient.setQueryData(
    ['event-students', event.public_id],
    wrapPaginated(students),
  );
  queryClient.invalidateQueries({ queryKey: ['my-students'] });
}

function reconcileGuardianSelf(
  queryClient: QueryClient,
  event: ReunificationEventSummary | null,
  guardians: SnapshotPayload['reunification_guardians'],
  currentUserId: number | null,
) {
  if (!event) {
    queryClient.removeQueries({ queryKey: ['guardian-self'], exact: false });
    return;
  }

  if (currentUserId === null) return;

  const self = guardians.find((g) => g.baseuser === currentUserId);
  if (!self) {
    // Current user has no guardian row on this event — let the detail
    // hook refetch and 404 itself rather than forging an empty object.
    queryClient.invalidateQueries({
      queryKey: ['guardian-self', event.public_id],
    });
    return;
  }

  // Project the subset that `GuardianSelf` consumers read. Extra fields
  // from the snapshot pass through harmlessly.
  const projected: GuardianSelf = {
    public_id: self.public_id,
    guardian_name: (self['guardian_name'] as string) ?? '',
    status: (self.status as GuardianStatus) ?? 'notified',
    status_display: (self['status_display'] as string) ?? '',
    arrived_at: (self['arrived_at'] as string | null) ?? null,
    verified_at: (self['verified_at'] as string | null) ?? null,
    student_count: (self['student_count'] as number) ?? 0,
  };
  queryClient.setQueryData(['guardian-self', event.public_id], projected);
}

function reconcileAlerts(
  queryClient: QueryClient,
  alerts: SnapshotAlertHistory[],
  roomStatuses: SnapshotRoomStatus[],
) {
  // There is no dedicated alert handler; seed the canonical keys
  // so any future consumer reads the authoritative snapshot state rather
  // than bootstrapping from an empty cache.
  queryClient.setQueryData(['alerts', 'active'], alerts);
  queryClient.setQueryData(['alert-room-statuses'], roomStatuses);
}

export function applySnapshot(snapshot: SnapshotPayload, deps: HandlerDeps) {
  const { queryClient, currentUserId } = deps;

  reconcileAlerts(queryClient, snapshot.active_alerts, snapshot.room_statuses);
  reconcileEvents(queryClient, snapshot.reunification_event);
  reconcileStudents(
    queryClient,
    snapshot.reunification_event,
    snapshot.reunification_students,
  );
  reconcileGuardianSelf(
    queryClient,
    snapshot.reunification_event,
    snapshot.reunification_guardians,
    currentUserId,
  );

  // Notifications aren't in the snapshot; refetch so counts reflect any
  // offline pushes surfaced via the normal `/notifications/` endpoint.
  queryClient.invalidateQueries({ queryKey: ['notifications'] });
  queryClient.invalidateQueries({ queryKey: ['unread-count'] });
}
